import { useState, useCallback } from "react";
import { FormErrors, SignupFormData } from "../types/authTypes";
import { validateEmail, validateDob } from "../utils/validation";

export const useSignupForm = () => {
  // ---------- State ----------
  const [formData, setFormData] = useState<SignupFormData>({
    user_name: "",
    name: "",
    organization_name: "",
    org_Arabic_Name: "",
    business_location: "",
    business_type: "",
    date_of_birth: "",
    phone_number: "",
    secondary_phone_number: "",
    address_line_1: "",
    email_id: "",
    secondary_email_id: "",
    state: "",
    postal_code: "",
    country: "",
    time_zone_id: "",
    roles: ["Owner"],
    filename: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [inputValue, setInputValue] = useState<string>("");

  // ---------- Phone Number Validation Helper ----------
  const validatePhoneNumber = useCallback((phoneNumber: string): string | undefined => {
    const numeric = phoneNumber.replace(/\D/g, "");

    if (!numeric) {
      return "Phone number is required";
    }
    if (numeric.length < 8) {
      return `Need ${8 - numeric.length} more digits (8-12 numbers required)`;
    }
    if (numeric.length > 12) {
      return "Phone number cannot exceed 12 digits";
    }
    return undefined; 
  }, []);

  // ---------- Helpers ----------
  const updateField = useCallback((field: keyof SignupFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setError = useCallback((field: keyof FormErrors, message?: string) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  }, []);

  const clearError = useCallback((field: keyof FormErrors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  // ---------- Email ----------
  const handleEmailChange = useCallback(
    (field: string, value: string) => {
      updateField(field as keyof SignupFormData, value);

      const isValid = validateEmail(value);
      setError(field as keyof FormErrors, isValid ? undefined : "Please enter a valid email address");
    },
    [updateField, setError],
  );

  // ---------- Phone (FIXED with real-time validation) ----------
  const handlePhoneChange = useCallback(
    (field: string, value: string) => {
      const numeric = value.replace(/\D/g, "");
      updateField(field as keyof SignupFormData, numeric);
      const error = validatePhoneNumber(numeric);
      setErrors((prev) => ({
        ...prev,
        [field]: error,
      }));
      return { isValid: !error, error };
    },
    [updateField, setError, validatePhoneNumber],
  );

  // ---------- DOB ----------
  const handleDobChange = useCallback(
    (value: string) => {
      const { formattedValue, date, errors: dobErrors } = validateDob(value);

      updateField("date_of_birth", date);
      setInputValue(formattedValue);

      setErrors((prev) => ({
        ...prev,
        day: dobErrors.day,
        month: dobErrors.month,
        date_of_birth: dobErrors.date_of_birth,
      }));
    },
    [updateField],
  );

  const getPhoneValidationMessage = useCallback((phoneNumber: string): string => {
    const numeric = phoneNumber.replace(/\D/g, "");
    if (!numeric) return "Phone number is required";
    if (numeric.length < 8) return `Need ${8 - numeric.length} more digits (8-12 numbers required)`;
    if (numeric.length > 12) return "Phone number cannot exceed 12 digits";
    return "✓ Valid phone number";
  }, []);

  return {
    formData,
    errors,
    inputValue,
    updateField,
    handleEmailChange,
    handlePhoneChange,
    handleDobChange,
    setError,
    clearError,
    validatePhoneNumber,
    getPhoneValidationMessage,
  };
};
